import Axios from 'axios';
import _pick from 'lodash/pick';
import { v4 as uuid } from 'uuid';

import { companyProfileSettingsInitialState } from 'store/companyProfileSettings';
import { HttpRoutes } from 'utils/constant';

export const companyModerationStorageKey = '_zippia-companyModerationChanges';

const enableCheckbox = (list = []) =>
  list.reduce((prev, { name, ...rest }) => ({ ...prev, [name]: { enabled: true, ...rest } }), {});

const mapQuestions = (list = []) =>
  list.reduce((prev, { question, ...rest }) => ({ ...prev, [question]: rest }), {});

const addID = (list = []) => list.map((item) => ({ id: uuid(), ...item }));

const getStatementFromArray = (data) => {
  const value = Array.isArray(data) ? data?.[0] : data;

  return value ?? '';
};

const companyProfileSettingService = {
  /**
   * Upload the file for CPS
   * @param {object} payload
   * @param {File} payload.file
   * @param {number} payload.companyID
   * @param {'COMPANY_PICTURE' | 'COMPANY_LOGO' | 'COMPANY_KEY_PEOPLE_PICTURE'} payload.type
   */
  async uploadImage(payload) {
    const { file, type, companyID } = payload;

    const formData = new FormData();

    formData.append('type', type);

    formData.append('file', file);

    formData.append('companyID', companyID);

    const apiUrl = HttpRoutes.cpsFileUpload;

    try {
      const response = await Axios.post(apiUrl, formData);
      const data = response.data.data || {};
      const imageURL = `${data.s3BucketURL}${data.NORMAL}`;
      return imageURL;
    } catch (err) {
      console.error(err);
      throw err;
    }
  },

  /**
   * Fits the data from server side to redux schema
   * @param {import('types').Company} companyInfo
   * @param {boolean} includeDeafults
   * @returns {typeof companyProfileSettingsInitialState}
   */
  fitDataToSchema(companyInfo = {}, includeDeafults = false) {
    let payload = {};

    if (includeDeafults) {
      payload = {
        ...companyProfileSettingsInitialState,
      };
    }

    return {
      ...payload,
      companyKeyPeopleNew: companyInfo.companyKeyPeopleNew || [],
      companyNews:
        addID(
          companyInfo?.companyNews?.sort((a, b) => new Date(b.submitTime) - new Date(a.submitTime))
        ) ?? [],
      companyVideos: companyInfo?.companyVideos || [],
      companyPictures: companyInfo?.companyPictures || [],
      cpsCompanyDescription: companyInfo?.cpsCompanyDescription || '',
      companyCoverImage: companyInfo?.companyCoverImage,
      companyMissionStatement: {
        companyMissionStatement: companyInfo?.companyMissionStatement,
        writtenCompanyDescription: getStatementFromArray(companyInfo?.writtenCompanyDescription),
      },
      benefits: {
        benefitsQuestions: mapQuestions(companyInfo?.benefitsQuestions),
        healthcareAndWellnessBenefits: enableCheckbox(companyInfo?.healthcareAndWellnessBenefits),
        retirementAndInsuranceBenefits: enableCheckbox(companyInfo?.retirementAndInsuranceBenefits),
        vacationBenefits: enableCheckbox(companyInfo?.vacationBenefits),
        familyFriendlyBenefits: enableCheckbox(companyInfo?.familyFriendlyBenefits),
        otherBenefits: enableCheckbox(companyInfo?.otherBenefits),
      },
      culture: {
        companyCharacteristics: addID(companyInfo?.companyCharacteristics),
        employeeInterviews: addID(companyInfo?.employeeInterviews),
        companyCulture: enableCheckbox(companyInfo?.companyCulture),
        diversityCulture: enableCheckbox(companyInfo?.diversityCulture),
        professionalDevelopmentBenefitsCulture: enableCheckbox(
          companyInfo?.professionalDevelopmentBenefitsCulture
        ),
      },
      details: _pick(companyInfo, [
        'companyLogo',
        'companyNameCPS',
        'companyPageTitle',
        'companyFoundedDate',
        'companyEmployeeSizeActual',
        'companyRevenueActual',
        'companyStreetAddress',
        'companyCity',
        'companyState',
        'companyStateCode',
        'companyPostalCode',
        'companyClickableWebsite',
        'careersPageURL',
        'companyIndustry',
        'companyType',
        'companySocialFacebook',
        'companySocialTwitterHandle',
        'companySocialLinkedin',
        'companySocialInstagram',
        'companySocialTiktok',
      ]),
      // This should have the same values as CONSTANTS.COMPANY_INFO_FIELD_TYPES.BOOLEAN.
      pageDisplay: _pick(companyInfo, [
        'indexable',
        'displayFlagOfCompanyJobsPage',
        'displayFlagOfCSP',
        'displayFlagOfCompanyRevenuePage',
        'displayFlagOfCompanyMissionStatementPage',
        'displayFlagOfCompanyLocationsPage',
        'displayFlagOfCompanyReviewsPage',
        'displayFlagOfCompanyHistoryPage',
        'displayFlagOfCompanyDemographicsPage',
        'displayFlagOfCompanyCompetitorsPage',
        'displayFlagOfCompetitorsJobsSections',
        'displayFlagOfPoliticalAffiliationSections',
        'displayFlagOfCompanyExecutivesPage',
        'displayFlagOfCompanyFAQPage',
      ]),
    };
  },

  getCompanyModerationState(data, isOld = false) {
    const companyModerationChanges = data.companyModerationChanges || {};
    const companyInfo = isOld
      ? data.companyInfo
      : {
          ...(data.companyInfo || {}),
          ...Object.keys(companyModerationChanges).reduce((acc, fieldName) => {
            acc[fieldName] = companyModerationChanges?.[fieldName]?.newVal?.[0];
            return acc;
          }, {}),
        };
    return companyInfo;
  },

  /**
   * Get the initial state of companyProfileSettings reducer
   * @param {'companyProfileSettings' | 'companyModerationAdminPage' | string} pageName
   * @param {object} data
   * @returns {typeof companyProfileSettingsInitialState}
   */
  getCompanyProfileSettingsState(pageName, data) {
    switch (pageName) {
      case 'companyModerationAdminPage': {
        const companyInfo = this.getCompanyModerationState(data);
        return this.fitDataToSchema(companyInfo);
      }
      case 'companyProfileSettings': {
        return this.fitDataToSchema(data.companyInfo, true);
      }
      default: {
        return companyProfileSettingsInitialState;
      }
    }
  },

  async publishChangesForModeration(adminUser, companyModerationChanges) {
    const apiUrl = adminUser ? HttpRoutes.companyModerationAdmin : HttpRoutes.companyModeration;
    try {
      await Axios.post(apiUrl, companyModerationChanges);
    } catch (err) {
      console.error(err);
      throw err;
    }
  },

  /**
   * Note: As of now this service method is not being used anywhere else
   * but may be used in future, so let's remove it for now.
   * @param {number} companyId
   * @param {string[]} hiddenSections List of sections which need to be hidden for CPP
   */
  async setCompanyDisplayMode(companyId, hiddenSections) {
    try {
      await Axios.post(HttpRoutes.setcompanydisplaymodeUser, {
        companyId,
        hiddenSections,
      });
    } catch (err) {
      console.error(err);
      throw err;
    }
  },

  saveChangesToCache(cacheKey, companyModerationChanges) {
    if (companyModerationChanges) {
      localStorage.setItem(cacheKey, JSON.stringify(companyModerationChanges));
    }
  },

  retrieveChangesFromCache(cacheKey) {
    const savedChanges = localStorage.getItem(cacheKey);

    try {
      localStorage.removeItem(cacheKey);
      return JSON.parse(savedChanges);
    } catch (err) {
      console.error(
        'Error occurred while retriving the chages for company moderation from cache',
        err
      );
      return null;
    }
  },

  async approveChanges(requestId, reviewedCompany) {
    try {
      await Axios.post(HttpRoutes.confirmReview, {
        reviewId: requestId,
        reviewedCompany: reviewedCompany,
      });
    } catch (err) {
      console.error(err);
      throw err;
    }
  },

  async rejectChanges(requestId, employerUserId) {
    try {
      await Axios.post(HttpRoutes.discardReview, {
        reviewId: requestId,
        userId: employerUserId,
      });
    } catch (err) {
      console.error(err);
      throw err;
    }
  },

  async quickApproveChanges(requestId, employerUserId) {
    try {
      await Axios.post(HttpRoutes.quickApproveChanges, {
        reviewId: requestId,
        userId: employerUserId,
      });
    } catch (err) {
      console.error(err);
      throw err;
    }
  },

  async deferChanges(requestId, employerUserId) {
    try {
      await Axios.post(HttpRoutes.deferReview, {
        reviewId: requestId,
        userId: employerUserId,
      });
    } catch (err) {
      console.error(err);
      throw err;
    }
  },
};

export default companyProfileSettingService;
